import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Menu, X, Globe } from 'lucide-react';
import { logoUrl } from '../data/mock';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img src={logoUrl} alt="Space Data" className="h-12 w-12 object-contain" />
            <span className={`text-2xl font-bold transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}>
              Space Data
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className={`font-medium transition-colors hover:text-orange-500 ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => scrollToSection('technology')}
              className={`font-medium transition-colors hover:text-orange-500 ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t.nav.technology}
            </button>
            <button
              onClick={() => scrollToSection('industries')}
              className={`font-medium transition-colors hover:text-orange-500 ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t.nav.industries}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`font-medium transition-colors hover:text-orange-500 ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`font-medium transition-colors hover:text-orange-500 ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* CTA & Language Toggle */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`transition-colors ${
                scrolled ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
              }`}
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'en' ? 'SQ' : 'EN'}
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {t.nav.getQuote}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={scrolled ? 'text-gray-700' : 'text-white'}
            >
              <Globe className="w-4 h-4" />
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 transition-colors ${
                scrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 bg-white border-t">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('services')}
                className="text-left font-medium text-gray-700 hover:text-orange-500 transition-colors"
              >
                {t.nav.services}
              </button>
              <button
                onClick={() => scrollToSection('technology')}
                className="text-left font-medium text-gray-700 hover:text-orange-500 transition-colors"
              >
                {t.nav.technology}
              </button>
              <button
                onClick={() => scrollToSection('industries')}
                className="text-left font-medium text-gray-700 hover:text-orange-500 transition-colors"
              >
                {t.nav.industries}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-left font-medium text-gray-700 hover:text-orange-500 transition-colors"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left font-medium text-gray-700 hover:text-orange-500 transition-colors"
              >
                {t.nav.contact}
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-orange-500 hover:bg-orange-600 text-white w-full"
              >
                {t.nav.getQuote}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
